import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import ArrowDownSvg from "@redotech/redo-icons/arrow-down_filled.svg";
import ArrowUpSvg from "@redotech/redo-icons/arrow-up_filled.svg";
import HelpCircleSvg from "@redotech/redo-icons/help-circle.svg";
import { assertNever } from "@redotech/util/type";
import classNames from "classnames";
import * as React from "react";
import { RedoCheckbox } from "../checkbox/redo-checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { Flex } from "../flex";
import { Text } from "../text";
import * as redoTableHeaderCellsCss from "./redo-table-header-cells.module.css";
import { ColumnAlignment } from "./redo-table-types";

export enum StandardRedoTableHeaderCellArrowMode {
  ASCENDING = "ascending",
  DESCENDING = "descending",
  NOT_SET = "not-set",
  NO_ARROW = "no-arrow",
}

type RedoTableSortDirection = "asc" | "desc";

export const sortDirectionToArrowMode: Record<
  RedoTableSortDirection,
  StandardRedoTableHeaderCellArrowMode
> = {
  asc: StandardRedoTableHeaderCellArrowMode.ASCENDING,
  desc: StandardRedoTableHeaderCellArrowMode.DESCENDING,
};

const alignmentToJustify: Record<
  ColumnAlignment,
  "flex-start" | "center" | "flex-end"
> = {
  [ColumnAlignment.LEFT]: "flex-start",
  [ColumnAlignment.CENTER]: "center",
  [ColumnAlignment.RIGHT]: "flex-end",
};

export const StandardRedoTableHeaderCell = ({
  headerText,
  helpIconTooltip,
  arrowIcon = StandardRedoTableHeaderCellArrowMode.NO_ARROW,
  onSortClick,
  alignment = ColumnAlignment.LEFT,
  "data-dd-action-name": actionName,
}: {
  "data-dd-action-name"?: string;
  headerText: string;
  helpIconTooltip?: React.ReactNode;
  arrowIcon?: StandardRedoTableHeaderCellArrowMode;
  onSortClick?: () => void;
  alignment?: ColumnAlignment;
}) => {
  const { t } = useTranslation();
  const justify = alignmentToJustify[alignment];
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = React.useState(false);
  const sortable = Boolean(onSortClick);

  const onMouseLeaves = useHandler((event: React.MouseEvent) => {
    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && headerRef.current?.contains(nextTarget)) {
      return;
    }

    setHovered(false);
  });
  const onMouseEnters = useHandler(() => setHovered(true));
  const onHeaderClick = useHandler(() => {
    onSortClick?.();
  });
  const onSortButtonClick = useHandler((event: React.MouseEvent) => {
    event.stopPropagation();
    onSortClick?.();
  });
  const onHelpIconClick = useHandler((event: React.MouseEvent) => {
    event.stopPropagation();
  });
  const onHelpIconKeyDown = useHandler((event: React.KeyboardEvent) => {
    event.stopPropagation();
  });
  const onHelpTooltipContentEvent = useHandler(
    (event: React.SyntheticEvent) => {
      event.stopPropagation();
    },
  );

  let arrow = undefined;
  switch (arrowIcon) {
    case StandardRedoTableHeaderCellArrowMode.ASCENDING:
      arrow = (
        <button
          className={redoTableHeaderCellsCss.arrowButton}
          onClick={onSortButtonClick}
          type="button"
        >
          <ArrowUpSvg
            className={classNames(
              redoTableHeaderCellsCss.icon,
              redoTableHeaderCellsCss.arrow,
            )}
          />
        </button>
      );
      break;
    case StandardRedoTableHeaderCellArrowMode.DESCENDING:
      arrow = (
        <button
          className={redoTableHeaderCellsCss.arrowButton}
          onClick={onSortButtonClick}
          type="button"
        >
          <ArrowDownSvg
            className={classNames(
              redoTableHeaderCellsCss.icon,
              redoTableHeaderCellsCss.arrow,
            )}
          />
        </button>
      );
      break;
    case StandardRedoTableHeaderCellArrowMode.NOT_SET:
    case StandardRedoTableHeaderCellArrowMode.NO_ARROW:
      if (hovered && sortable) {
        arrow = (
          <div className={redoTableHeaderCellsCss.arrowButton}>
            <div
              className={classNames(
                redoTableHeaderCellsCss.icon,
                redoTableHeaderCellsCss.arrow,
                redoTableHeaderCellsCss.hoverArrow,
              )}
            >
              <ArrowDownSvg />
            </div>
          </div>
        );
      }
      break;
    default:
      assertNever(arrowIcon);
  }
  const text = (
    <Text fontSize="xxs" fontWeight="medium" textColor="secondary">
      {headerText}
    </Text>
  );

  const textElement = onSortClick ? (
    <button
      className={redoTableHeaderCellsCss.textButton}
      onClick={onSortButtonClick}
      onMouseEnter={onMouseEnters}
      onMouseLeave={onMouseLeaves}
      type="button"
    >
      {text}
    </button>
  ) : (
    <div className={redoTableHeaderCellsCss.textButton}>{text}</div>
  );

  const helpIcon = helpIconTooltip && (
    <div className={redoTableHeaderCellsCss.iconWrapper}>
      <Tooltip>
        <TooltipTrigger
          aria-label={t("About {{header}}", {
            context: "table header",
            header: headerText,
          })}
          className={classNames(
            redoTableHeaderCellsCss.iconButton,
            redoTableHeaderCellsCss.icon,
            redoTableHeaderCellsCss.help,
          )}
          onClick={onHelpIconClick}
          onKeyDown={onHelpIconKeyDown}
          type="button"
        >
          <HelpCircleSvg />
        </TooltipTrigger>
        <TooltipContent
          className="max-w-xs text-left"
          onClick={onHelpTooltipContentEvent}
          onMouseDown={onHelpTooltipContentEvent}
          onMouseUp={onHelpTooltipContentEvent}
          onPointerDown={onHelpTooltipContentEvent}
          onPointerUp={onHelpTooltipContentEvent}
          side="bottom"
          sideOffset={4}
        >
          {helpIconTooltip}
        </TooltipContent>
      </Tooltip>
    </div>
  );

  // For right-aligned columns, put the arrow before the text
  const isRightAligned = alignment === ColumnAlignment.RIGHT;

  return (
    <Flex
      align="center"
      className={classNames(
        redoTableHeaderCellsCss.headerCell,
        sortable && redoTableHeaderCellsCss.sortableHeaderCell,
      )}
      data-dd-action-name={actionName}
      dir="row"
      gap="xs"
      grow="1"
      justify={justify}
      onClick={sortable ? onHeaderClick : undefined}
      onMouseEnter={onMouseEnters}
      onMouseLeave={onMouseLeaves}
      overflow="hidden"
      p="xl"
      ref={headerRef}
    >
      {isRightAligned && arrow}
      {textElement}
      {helpIcon}
      {!isRightAligned && arrow}
    </Flex>
  );
};

export const RedoTableCheckboxHeaderCell = ({
  numRows,
  rowsSelected,
  setRowsSelected,
  disabled,
  "data-dd-action-name": actionName,
}: {
  "data-dd-action-name"?: string;
  numRows: number;
  rowsSelected: boolean[];
  setRowsSelected: (value: boolean[]) => void;
  disabled?: boolean;
}) => {
  return (
    <Flex
      align="center"
      as="label"
      className={classNames(redoTableHeaderCellsCss.headerCell)}
      data-arbiter="RedoTableCheckboxHeaderCell"
      data-dd-action-name={actionName}
      justify="center"
      p="xl"
    >
      <RedoCheckbox
        disabled={disabled}
        setValue={(value) => {
          const truthiness = value === "indeterminate" ? false : value;
          setRowsSelected(Array.from({ length: numRows }, () => truthiness));
        }}
        size="sm"
        value={rowsSelected.length > 0 && rowsSelected.every(Boolean)}
      />
    </Flex>
  );
};

export function EmptyHeaderCell() {
  return <span className={classNames(redoTableHeaderCellsCss.headerCell)} />;
}

export function CustomHeaderCell({ children }: { children: React.ReactNode }) {
  return (
    <div className={classNames(redoTableHeaderCellsCss.headerCell)}>
      {children}
    </div>
  );
}
